import React from 'react';
import { Alert, Box, Checkbox, Text } from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import {
  BulkUploadTypes,
  UploadTypeConfig,
  UploadTypeConfigCallback,
  UploadTypeConfigCallbackParams,
} from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { useFormWithProvider } from 'forms';
import api from 'api';
import { getContractorText } from 'utils/getContractorText';
import { getPeoplePage } from 'utils/links';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { employeeSubTypeLabels, employeeSubTypes } from 'constants/employeeSubType';
import { isEmployeeSubTypeEnabled } from 'hooks/useEmployeeSubTypeEnabled';
import { getCustomFieldPreviewColumns } from './customFieldPreviewColumns';

const FIXED_TERM_FILE_ROW_VALUES = ['fixed term', employeeSubTypes.fixedTerm];

const getEmployeeTypeLabel = (employeeSubType: unknown) =>
  FIXED_TERM_FILE_ROW_VALUES.includes(
    String(employeeSubType ?? '')
      .trim()
      .toLowerCase(),
  )
    ? employeeSubTypeLabels[employeeSubTypes.fixedTerm]
    : employeeSubTypeLabels[employeeSubTypes.fullTime];

const getSheet0Columns = (
  data: UploadTypeConfigCallbackParams,
): NonNullable<UploadTypeConfig['preview']['columns']> => {
  return [
    {
      title: 'Name',
      render: (item) => item.file_row?.commonProperties?.employeeName,
    },
    ...(isEmployeeSubTypeEnabled(data.featureFlags)
      ? ([
          {
            title: 'Employee Type',
            render: (item) => getEmployeeTypeLabel(item.file_row?.employeeSubType),
          },
        ] as NonNullable<UploadTypeConfig['preview']['columns']>)
      : []),
    {
      title: 'Employee ID',
      render: (item) => item.file_row?.commonProperties?.employeeId,
    },
    {
      title: 'Email ID',
      render: (item) => item.file_row?.commonProperties?.email,
    },
    {
      title: 'Date of Hiring',
      render: (item) => item.file_row?.dateOfHiring,
    },
    data.featureFlags?.esicMultiLocation
      ? {
          title: 'Office Location',
          render: (item) => item.file_row?.officeLocationName,
        }
      : {
          title: 'Location',
          render: (item) => item.file_row?.location,
        },

    ...(data.featureFlags?.reArchM3S1
      ? [
          {
            title: 'Salary Structure',
            render: (
              item: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0],
            ) => item.file_row?.salaryStructure,
          },
        ]
      : []),
    {
      title: 'Phone Number',
      render: (item) => item.file_row?.phone,
    },
    {
      title: 'Gender',
      render: (item) => item.file_row?.gender,
    },
    {
      title: 'Date of Birth',
      render: (item) => item.file_row?.dob,
    },
    {
      title: 'Bank Account',
      render: (item) => item.file_row?.bankAccount,
    },
    {
      title: 'Bank IFSC',
      render: (item) => item.file_row?.bankIfsc,
    },
    {
      title: 'Annual CTC',
      render: (item) => item.file_row?.annualCtc,
    },
    {
      title: 'PAN',
      render: (item) => item.file_row?.pan,
    },
    {
      title: 'PF Registration',
      render: (item) => item.file_row?.pfStatus,
    },
    {
      title: 'PF UAN',
      render: (item) => item.file_row?.pfUan,
    },
    {
      title: 'Aadhar Number',
      render: (item) => item.file_row?.aadharNumber,
    },
    {
      title: 'PT Enabled',
      render: (item) => (item.file_row?.ptEnabled ? 'Yes' : 'No'),
    },
    {
      title: 'PT Location',
      render: (item) => item.file_row?.ptLocation,
    },
    {
      title: 'LWF Enabled',
      render: (item) => (item.file_row?.lwfEnabled ? 'Yes' : 'No'),
    },
    {
      title: 'LWF Location',
      render: (item) => item.file_row?.lwfLocation,
    },
    {
      title: 'ESIC Registration Status',
      render: (item) => item.file_row?.esicStatus,
    },
    {
      title: 'ESIC IP Number',
      render: (item) => item.file_row?.esicIpNumber,
    },
    {
      title: 'NPS Enabled',
      render: (item) => item.file_row?.npsEnabled,
    },
    {
      title: 'PRAN Number',
      render: (item) => item.file_row?.pranNumber,
    },
    {
      title: 'Designation',
      render: (item) => item.file_row?.designation,
    },
    {
      title: 'Department',
      render: (item) => item.file_row?.department,
    },
    {
      title: 'Manager',
      render: (item) => item.file_row?.manager,
    },
    ...getCustomFieldPreviewColumns(data),
  ];
};

const sheet1Columns: UploadTypeConfig['preview']['columns'] = [
  {
    title: 'Name',
    render: (item) => item.file_row?.commonProperties?.employeeName,
  },
  {
    title: 'Email ID',
    render: (item) => item.file_row?.commonProperties?.email,
  },
  {
    title: 'PAN',
    render: (item) => item.file_row?.pan,
  },
  {
    title: 'Phone Number',
    render: (item) => item.file_row?.phone,
  },
  {
    title: 'Gender',
    render: (item) => item.file_row?.gender,
  },
  {
    title: 'Date of Birth',
    render: (item) => item.file_row?.dob,
  },
  {
    title: 'Bank Account',
    render: (item) => item.file_row?.bankAccount,
  },
  {
    title: 'Bank IFSC',
    render: (item) => item.file_row?.bankIfsc,
  },
];

const onSubmitInitiation: UploadTypeConfig['initiation']['onSubmitInitiation'] = async ({
  extraFieldsData,
  onSuccessCb,
  bulkUploadRecord,
}) => {
  if (bulkUploadRecord?.id) {
    try {
      await api.bulkUploads.updateConfig({
        bulkUploadId: bulkUploadRecord.id,
        filters: {
          shouldSendWelcomeEmail: extraFieldsData.formData.sendWelcomeEmails ?? false,
        },
      });
      onSuccessCb?.();
    } catch (error) {
      //Do nothing, error will be logged in sentry
    }
  }
};

const ExtraFields: NonNullable<UploadTypeConfig['initiation']['ExtraFields']> = () => {
  const { register, formData: formDataValues } = useFormWithProvider<{
    sendWelcomeEmails: boolean;
  }>();
  return (
    <Box marginTop="spacing.8">
      <Box display="flex" alignItems="center" gap="spacing.2">
        <Checkbox
          isChecked={formDataValues.sendWelcomeEmails}
          {...register('sendWelcomeEmails', 'checkbox')}
        />
        <Text color="surface.text.gray.subtle">Send welcome emails to added employees</Text>
      </Box>
      <Alert
        isDismissible={false}
        isFullWidth
        marginTop="spacing.8"
        color="neutral"
        description="By uploading employee details, you agree that we will verify their PAN and bank account information for accuracy and compliance."
      />
    </Box>
  );
};

const config: UploadTypeConfigCallback = (data) => ({
  permissions: [permissions.EMPLOYEE_ADD],
  initiation: {
    feature: 'bulkOnboardingFlag',
    permissions: [],
    pageTitle: 'Bulk Onboard Employees',
    step1Title: '1. Download the Onboarding template',
    step1Subtitle: `Download the template and open it in Excel. Fill in employee or ${
      getContractorText(data).contractor
    } details, save the file, and upload it here.`,
    instructions: [
      'The template has two tabs. Fill out both as needed.',
      'Do not add or remove any columns.',
      'There is a limit of 500 employees per upload',
      "If a field is empty, leave it blank (don't enter '-', 'NA,' etc.).",
      'Avoid using all capital letters for names, addresses, titles, etc.',
      'Mandatory fields are mentioned with * sign',
      'Date should be mentioned in DD/MM/YYYY format',
      "Bank account number should be added with leading '0' if applicable",
    ],
    onSampleTemplateDownload() {
      window.open(`/v2/api/bulk-onboarding/download-template`, '_blank');
    },
    ExtraFields,
    onSubmitInitiation,
  },
  preview: {
    sheets: {
      Employees: { columns: getSheet0Columns(data) },
      [getContractorText(data).contractorsTitleCase]: {
        columns: sheet1Columns,
      },
    },
    showColumnsSelector: true,
    permissions: [permissions.EMPLOYEE_ADD],
    feature: 'bulkOnboardingFlag',
    renderConfirmationText: (data) =>
      `Almost done! Please confirm to proceed with the bulk employee onboarding process.`,
    successModalCtaText: 'Go to People',
    successModalMainText: "You've added employees to your team",
    successModalSubText: () =>
      'Need to make changes? Edit employee info anytime from the "People" page',
    onSuccessModalCtaClick() {
      const peoplePageUrl = getPeoplePage(data.featureFlags?.employeeOnboarding);
      data.history.push(peoplePageUrl);
    },
    onDownloadErrorReport(history) {
      history?.push({
        pathname: routePaths.bulkUploads.initiate,
        search: `type=${BulkUploadTypes.TYPE_BULK_ONBOARDING}`,
      });
    },
  },
});

export default config;
